export default () => ({
  show: false,
  error: null,
  wishlist: [],
  productList: [],
  wishCount: 0,
  totalAmount: 0,

  get formattedTotalAmount() {
    const locale = this.getLocale();
    const currencySymbol = this.getCurrencySymbol();

    if (!currencySymbol) {
      return this.totalAmount.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }

    return `${this.totalAmount.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ${currencySymbol}`;
  },

  getLocale() {
    // Try to get locale from Shopify data
    if (window.shopifyData && window.shopifyData.locale) {
      return window.shopifyData.locale;
    }

    // Try to get locale from the HTML lang attribute
    const htmlLang = document.documentElement.lang;
    if (htmlLang) {
      return htmlLang;
    }

    // Fallback to Swedish
    return "sv-SE";
  },

  getCurrencySymbol() {
    // Try to get currency symbol from Shopify data
    if (window.shopifyData && window.shopifyData.currencySymbol) {
      return window.shopifyData.currencySymbol;
    }

    // Try to get currency from Shopify data and format it
    if (window.shopifyData && window.shopifyData.currency) {
      const currency = window.shopifyData.currency;

      // Only convert Nordic currencies to "kr", leave others as they are
      if (["SEK", "DKK", "NOK"].includes(currency)) {
        return "kr";
      }
      return currency;
    }

    // Return empty string if no currency info available
    return "";
  },

  toggle() {
    this.show = !this.show;
    Grebify.noScroll(!this.show);
  },

  setProduct(event) {
    const wishlist = this.getWishlistFromLocalStorage();
    const foilData = this.getFoilDataFromLocalStorage();
    const existingItem = this.findWishlistItem(
      wishlist,
      event.detail.productId,
      event.detail.variantId
    );

    if (existingItem) {
      this.increaseProductQuantity(existingItem, event.detail.quantity);
    } else {
      this.addNewProductToWishlist(
        wishlist,
        event.detail.handle,
        event.detail.productId,
        event.detail.variantId,
        event.detail.quantity,
        foilData[0]?.data
      );
    }

    this.updateWishlistInLocalStorage(wishlist);
    this.updateWishCount();
    this.loadWishlist();
    this.toggle();

    // Assuming "foil" is just a part of the item data, no need to remove it
    localStorage.removeItem("foil"); // If this is no longer needed
  },

  fetchProductDetails(handle, variantId) {
    const url = `${Shopify.routes.root}products/${handle}.js`;

    return fetch(url)
      .then((response) => this.handleFetchResponse(response))
      .then((product) => this.getProductVariant(product, variantId))
      .catch((error) => this.handleFetchError(error));
  },

  loadWishlist() {
    const wishlist = this.getWishlistFromLocalStorage();
    this.wishlist = wishlist;
    this.productList = [];
    this.totalAmount = 0;

    // Use Promise.all with map to maintain order, but reverse the wishlist to show latest first
    Promise.all(
      wishlist
        .slice()
        .reverse()
        .map((item) =>
          this.fetchProductDetails(item.handle, item.variantId).then(
            ({ product, variant }) => {
              if (product && variant) {
                return { item, product, variant };
              }
              return null;
            }
          )
        )
    ).then((results) => {
      // Add products to list in the correct order (latest first)
      results.forEach((result) => {
        if (result) {
          this.addProductToList(
            result.item,
            result.product,
            result.variant,
            result.item.foil
          );
        }
      });
      this.updateWishCount();
    });
  },

  removeFromWishlist(productId, variantId) {
    const wishlist = this.getWishlistFromLocalStorage();
    const index = this.findWishlistItemIndex(wishlist, productId, variantId);

    if (index !== -1) {
      wishlist.splice(index, 1)[0];
      this.updateWishlistInLocalStorage(wishlist);
      this.updateWishCount();
      this.loadWishlist();
    }
  },

  init() {
    this.loadWishlist();
  },

  // Helper functions
  getWishlistFromLocalStorage() {
    return JSON.parse(localStorage.getItem("wishlist")) || [];
  },
  getFoilDataFromLocalStorage() {
    return JSON.parse(localStorage.getItem("foil")) || [];
  },

  updateWishlistInLocalStorage(wishlist) {
    localStorage.setItem("wishlist", JSON.stringify(wishlist));
  },

  findWishlistItem(wishlist, productId, variantId) {
    return wishlist.find(
      (item) => item.productId === productId && item.variantId === variantId
    );
  },

  findWishlistItemIndex(wishlist, productId, variantId) {
    return wishlist.findIndex(
      (item) => item.productId === productId && item.variantId === variantId
    );
  },

  increaseProductQuantity(item, quantity) {
    if (item.quantity === 1) {
      item.quantity += 1;
    } else {
      item.quantity += quantity;
    }
  },

  addNewProductToWishlist(
    wishlist,
    handle,
    productId,
    variantId,
    quantity,
    foil
  ) {
    wishlist.push({ handle, productId, variantId, quantity, foil });
  },

  handleFetchResponse(response) {
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    return response.json();
  },

  getProductVariant(product, variantId) {
    const variant = product.variants.find((v) => v.id == variantId);
    return { product, variant };
  },

  handleFetchError(error) {
    this.error = error;
    console.error("There has been a problem with your fetch operation:", error);
  },

  addProductToList(item, product, variant, foil) {
    // Check if variant and price exist
    if (!variant || typeof variant.price === "undefined") {
      console.error("Variant or price is undefined:", {
        variant,
        product,
        item,
      });
      return;
    }

    const totalPrice = (variant.price * item.quantity) / 100;
    const locale = this.getLocale();
    const currencySymbol = this.getCurrencySymbol();

    let formattedTotalPrice;
    if (!currencySymbol) {
      formattedTotalPrice = totalPrice.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else {
      formattedTotalPrice = `${totalPrice.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} ${currencySymbol}`;
    }

    this.productList.push({
      ...item,
      product,
      variant,
      totalPrice,
      formattedTotalPrice,
      foil,
    });
    this.totalAmount += totalPrice;
  },

  updateWishCount() {
    this.wishCount = this.wishlist.reduce(
      (total, item) => total + item.quantity,
      0
    );
    // Dispatch custom event to update wishCount across all instances
    this.$dispatch("wish-count-updated", { count: this.wishCount });
  },
});
